import {
  Button,
  CircularProgress,
  Dialog,
  FormGroup,
  FormLabel,
  Grid,
  IconButton,
  Slide,
  TextField
} from '@material-ui/core'
import { TransitionProps } from '@material-ui/core/transitions'
import { Close } from '@material-ui/icons'
import { useFormFields } from 'hooks/useFormFields'
import { useNewsletter } from 'hooks/useNewsletter'
import { useRecaptcha } from 'hooks/useRecaptcha'
import React, { useEffect, useRef, useState } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import { useIsMobile } from 'shared/hooks'
import { configurationsService } from 'shared/services/ConfigurationsService'
import { TextService } from 'shared/services/TextService'
import { useLanguage } from 'store/locale/hooks/useLanguage'
import { Title } from '../Typography/Title'
import { useStyles } from './NewsLetterSubscriptionModal.styles'

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children?: React.ReactElement<any, any> },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />
})

export interface INewsLetterModal {
  t: (key: string) => string
  isOpen: boolean
  onClose: () => void
  type: string
}

export const NewsletterSubscriptionModal = ({ t, isOpen, onClose, type }: INewsLetterModal) => {
  const [isFormSubmitted, setIsFormSubmitted] = useState(false)
  const [isValidForm, setIsValidForm] = useState(false)
  const isMobile = useIsMobile()
  const classes = useStyles()
  const { language } = useLanguage()

  const recaptchaRef = useRef<ReCAPTCHA>()

  const { subscribe, isLoading: newsletterIsLoading, success: newsletterSuccess } = useNewsletter()
  const { isLoading: recaptchaIsLoading, siteverify, success: recaptchaSuccess } = useRecaptcha()

  const isLoading = newsletterIsLoading || recaptchaIsLoading
  const success = newsletterSuccess && recaptchaSuccess

  const { fields, handleFieldChange } = useFormFields({
    lastname: '',
    firstname: '',
    email: '',
    company: '',
    language
  })

  useEffect(() => {
    setIsValidForm(fields.email.length > 0 && TextService.validateEmail(fields.email) && fields.lastname.length > 0)
  }, [fields.email, fields.firstname, fields.lastname, fields.company])

  useEffect(() => {
    // @ts-ignore
    if (onClose) {
      fields.email = ''
      fields.lastname = ''
      fields.firstname = ''
      fields.company = ''
    }
  }, [onClose])

  const onSubmit = async (e) => {
    e.preventDefault()

    if (isValidForm) {
      const token = await recaptchaRef.current.executeAsync()
      recaptchaRef.current.reset()

      if (token) {
        setIsFormSubmitted(true)

        const isTokenValid = await siteverify(token)
        if (isTokenValid) {
          await subscribe(fields)
        }
      }
    }
  }

  const handleClose = () => {
    onClose()
    setTimeout(() => {
      setIsValidForm(false)
      setIsFormSubmitted(false)
    }, 500)
  }

  return (
    <Dialog
      TransitionComponent={Transition}
      open={isOpen}
      onClose={onClose}
      maxWidth={'lg'}
      fullScreen={isMobile}
      fullWidth={isMobile}
      classes={{
        paper: classes.paper
      }}
    >
      {!isMobile && (
        <IconButton aria-label={'close'} onClick={handleClose} className={classes.closeButton}>
          <Close />
        </IconButton>
      )}
      <Grid
        container
        className={classes.modal}
        alignItems={'center'}
        justifyContent={!isFormSubmitted ? 'space-between' : 'flex-start'}
        wrap="nowrap"
        item
      >
        <div className={classes.modalTitle}>
          <Title className={classes.title}>{t('subscribeTitle')}</Title>
        </div>

        {!isFormSubmitted && (
          <>
            <form onSubmit={onSubmit} className={classes.newsletterForm}>
              {type === 'subscribe' && (
                <>
                  <FormGroup>
                    <FormLabel className={classes.inputLabel}>{t('lastname')}</FormLabel>
                    <TextField
                      className={classes.input}
                      id={'lastname'}
                      size="small"
                      variant="outlined"
                      placeholder={t('lastname')}
                      name={'lastname'}
                      onChange={handleFieldChange}
                      value={fields.lastname}
                    />
                  </FormGroup>
                  <FormGroup>
                    <FormLabel className={classes.inputLabel}>{t('firstname')}</FormLabel>
                    <TextField
                      className={classes.input}
                      id={'firstname'}
                      size="small"
                      variant="outlined"
                      placeholder={t('firstname')}
                      name={'firstname'}
                      onChange={handleFieldChange}
                      value={fields.firstname}
                    />
                  </FormGroup>
                </>
              )}
              <FormGroup>
                <FormLabel className={classes.inputLabel}>{t('company')}</FormLabel>
                <TextField
                  className={classes.input}
                  id={'company'}
                  size="small"
                  variant="outlined"
                  placeholder={t('company')}
                  name={'company'}
                  value={fields.company}
                  onChange={handleFieldChange}
                />
              </FormGroup>
              <FormGroup>
                <FormLabel className={classes.inputLabel}>{t('emailSubscribe')}</FormLabel>
                <TextField
                  className={classes.input}
                  id={'email'}
                  size="small"
                  variant="outlined"
                  placeholder={t('email')}
                  name={'email'}
                  value={fields.email}
                  onChange={handleFieldChange}
                  helperText={t('required')}
                />
              </FormGroup>

              <div className="w-full flex justify-center z-50">
                <ReCAPTCHA ref={recaptchaRef} sitekey={configurationsService.recaptchaPublicKey} size="invisible" />
              </div>
              <Grid container item justifyContent={'center'}>
                <Button
                  type={'submit'}
                  value={t('subscribe')}
                  placeholder={t('subscribe')}
                  className={classes.subscribeButton}
                  disabled={!isValidForm}
                >
                  {t('subscribeButton')}
                </Button>
              </Grid>
            </form>
            {isMobile && (
              <Grid container item justifyContent={'center'}>
                <Button variant="outlined" onClick={handleClose} type="button" className={classes.button}>
                  Fermer
                </Button>
              </Grid>
            )}
            {type === 'subscribe' && (
              <small className={classes.legalMentions}>
                {t('subscriptionLegalMentions')}
                <span>
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classes.link}
                    href={`${
                      language === 'fr'
                        ? 'https://www.iliad.fr/fr/donnees-personnelles'
                        : 'https://www.iliad.fr/en/donnees-personnelles'
                    }`}
                  >
                    {t('confidentiality')}
                  </a>
                </span>
                {t('confidentiality-end')}
              </small>
            )}
          </>
        )}

        {isFormSubmitted && isLoading && (
          <div className={classes.blocWrapper}>
            <CircularProgress size="5rem" className={classes.loader} />
          </div>
        )}

        {isFormSubmitted && !isLoading && success && (
          <div className={classes.afterFormRegistration}>
            <Title className={classes.thanksTitle}>{t('thankYou')}</Title>
            {type === 'subscribe' && <p>{t('subscription-success')}</p>}
          </div>
        )}

        {isFormSubmitted && !isLoading && !success && (
          <div className={classes.blocWrapper}>
            <Title>Une erreur s'est produite, veuillez reéssayer</Title>
            {isMobile && (
              <Grid container item justifyContent={'center'}>
                <Button variant="outlined" onClick={handleClose} type="button" className={classes.button}>
                  Fermer
                </Button>
              </Grid>
            )}
          </div>
        )}
      </Grid>
    </Dialog>
  )
}
